import React from 'react';
import { ArrowRight, FileText, ShieldCheck, Sparkles, Star } from 'lucide-react';
import { Button } from './ui/button';
import TelegramButton from './TelegramButton';
import WhatsAppButton from './WhatsAppButton';
import { useQualification } from '../contexts/QualificationContext';
import { GLOBAL_STATS, fmtEUR } from '../lib/financial';

/**
 * Final CTA block — last conversion push before the footer.
 * Designed as a high-contrast section with multiple action paths:
 *   1. Primary: open qualification modal (60s funnel)
 *   2. Secondary: scroll to materials download
 *   3. Tertiary: Telegram chat
 *
 * Includes microcopy trust badges to defuse last-second hesitation.
 */
export default function FinalCTASection() {
  const { open } = useQualification();

  return (
    <section
      className="relative px-4 md:px-12 py-20 md:py-28 overflow-hidden bg-[var(--gp-forest)] text-[hsl(var(--primary-foreground))]"
      data-testid="final-cta-section"
    >
      {/* Subtle radial accent in the background */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 75% 30%, rgba(201,163,74,0.18) 0%, transparent 55%), radial-gradient(circle at 20% 75%, rgba(255,255,255,0.04) 0%, transparent 60%)',
        }}
      />

      <div className="relative max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-[var(--gp-gold)] font-semibold">
          <Sparkles className="w-3.5 h-3.5" /> Ultimo passo
        </div>

        <h2 className="font-serif text-3xl md:text-5xl lg:text-[3.5rem] leading-[1.05] mt-3 md:mt-4">
          Pronto a possedere il tuo
          <br />
          <span className="italic text-[var(--gp-gold-soft,#E8C97A)]">angolo di paradiso?</span>
        </h2>

        <p className="text-sm md:text-base mt-4 md:mt-5 max-w-2xl mx-auto opacity-85 leading-relaxed">
          {GLOBAL_STATS.totalUnits} appartamenti tra Indonesia, Albania e Thailandia. Quote da {fmtEUR(40000)} o
          proprietà da {fmtEUR(GLOBAL_STATS.minDeposit)} anticipo. <b>60 secondi</b> per scoprire il tuo percorso —
          nessun impegno, brochure e financing inclusi.
        </p>

        {/* Trust strip */}
        <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] md:text-xs bg-white/8 backdrop-blur-sm rounded-full px-4 py-2 border border-white/10">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-[var(--gp-gold-soft,#E8C97A)]" />
            Nessun anticipo richiesto
          </span>
          <span className="text-white/30 hidden sm:inline">·</span>
          <span className="inline-flex items-center gap-1.5">
            <Star className="w-3.5 h-3.5 fill-[#00B67A] text-[#00B67A]" />
            Eccellente su Trustpilot
          </span>
          <span className="text-white/30 hidden sm:inline">·</span>
          <span className="inline-flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-[var(--gp-gold-soft,#E8C97A)]" />
            PDF & financing inclusi
          </span>
        </div>

        {/* CTAs — UNIFICATI: tutto apre il modulo "Parla con un Senior Associate" */}
        <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-2xl mx-auto">
          <Button
            size="lg"
            className="h-12 md:h-14 px-6 md:px-8 text-base md:text-lg font-semibold bg-[var(--gp-gold)] hover:bg-[var(--gp-gold-hover,#B8932F)] text-[var(--gp-forest)] shadow-2xl whitespace-nowrap w-full sm:w-auto"
            onClick={() => open('final_cta_primary')}
            data-testid="final-cta-primary"
          >
            Parla con un Senior Associate <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* WhatsApp + Telegram side-by-side (Customer Care channels) */}
        <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
          <span className="text-[12px] md:text-sm text-white/65">Preferisci messaggiare?</span>
          <WhatsAppButton
            variant="ghost"
            size="sm"
            label="WhatsApp"
            source="final_cta"
            className="!text-[#25D366] hover:!text-[#25D366] !p-0 !h-auto !rounded-none underline-offset-4 hover:underline"
          />
          <span className="text-white/30">·</span>
          <TelegramButton
            variant="ghost"
            size="sm"
            label="Telegram"
            source="final_cta"
            className="!text-[var(--gp-gold-soft,#E8C97A)] hover:!text-[var(--gp-gold-soft,#E8C97A)] !p-0 !h-auto !rounded-none underline-offset-4 hover:underline"
          />
          <span className="text-white/30">·</span>
          <span className="text-[12px] md:text-sm text-white/65">Risposta in 1–2 ore lavorative</span>
        </div>

        {/* Urgency hint */}
        <p className="mt-7 text-[11px] md:text-xs uppercase tracking-[0.22em] text-[var(--gp-gold-soft,#E8C97A)]/85 font-semibold">
          ⚡ Consegna 2027-2029 · Piano pagamenti rateale · Zero mutui bancari
        </p>
      </div>
    </section>
  );
}
